import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    TextField,
    InputAdornment,
    Snackbar,
    Alert,
} from '@mui/material';
import { RefreshCw, Search } from 'lucide-react';
import {
    StyledContainer,
    StyledCard,
    StyledTableContainer,
    StyledTableRow,
    StyledTableCell,
    StyledTableHeadCell,
} from '../../../styles/purchaseInvoices/pages/commonTableStyles';
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import useFetchWithMsal from '../../../hooks/useFetchWithMsal';
import { protectedResources } from '../../../authConfig';

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const SuppliersTab = () => {
    const { isDesktop } = useResponsive();
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.read,
    });

    const [suppliers, setSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchSuppliers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await execute('GET', `${API_BASE_URL}/api/suppliers`);
            if (data?.suppliers) {
                setSuppliers(data.suppliers);
                const maxDate = data.suppliers
                    .map(s => s.lastUpdated)
                    .filter(Boolean)
                    .sort()
                    .at(-1);
                if (maxDate) setLastSyncTime(new Date(maxDate));
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'שגיאה בטעינת הספקים', severity: 'error' });
        } finally {
            setIsLoading(false);
            setLoaded(true);
        }
    }, [execute]);

    useEffect(() => {
        if (!loaded) {
            fetchSuppliers();
        }
    }, [loaded, fetchSuppliers]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const data = await execute('POST', `${API_BASE_URL}/api/suppliers/refresh`);
            setSnackbar({
                open: true,
                message: `סונכרנו ${data.count} ספקים בהצלחה`,
                severity: 'success',
            });
            setLastSyncTime(new Date(data.syncedAt));
            await fetchSuppliers();
        } catch (err) {
            setSnackbar({ open: true, message: 'שגיאה בסנכרון הספקים', severity: 'error' });
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
    };

    const filteredSuppliers = suppliers.filter(
        s =>
            s.supNum?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.supName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading && !loaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography variant="h6" color="textSecondary">טוען ספקים...</Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            <StyledCard>
                {/* Header row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}>
                    <TextField
                        placeholder="חיפוש לפי מספר ספק או שם..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        size="small"
                        dir="rtl"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={16} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', md: 300 } }}
                    />
                    <Button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshCw size={18} />}
                        sx={{ minWidth: 160 }}
                    >
                        {isRefreshing ? 'מסנכרן...' : 'רענן ספקים'}
                    </Button>
                </Box>

                {/* Table */}
                {isDesktop ? (
                    <StyledTableContainer>
                        <Table dir="rtl" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledTableHeadCell align="right" sx={{ width: 180 }}>
                                        מספר ספק
                                    </StyledTableHeadCell>
                                    <StyledTableHeadCell align="right">
                                        שם ספק
                                    </StyledTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSuppliers.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={2} align="center" sx={{ py: 5 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {suppliers.length === 0
                                                    ? 'לחץ על "רענן ספקים" כדי לטעון את רשימת הספקים מפריוריטי'
                                                    : 'לא נמצאו ספקים תואמים לחיפוש'}
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    filteredSuppliers.map(s => (
                                        <StyledTableRow key={s.supNum}>
                                            <StyledTableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                                {s.supNum}
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {s.supName}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                ) : (
                    /* Mobile card view */
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {filteredSuppliers.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    {suppliers.length === 0
                                        ? 'לחץ על "רענן ספקים" כדי לטעון את הרשימה'
                                        : 'לא נמצאו ספקים תואמים'}
                                </Typography>
                            </Box>
                        ) : (
                            filteredSuppliers.map(s => (
                                <Box
                                    key={s.supNum}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.paper',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                                        {s.supNum}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {s.supName}
                                    </Typography>
                                </Box>
                            ))
                        )}
                    </Box>
                )}

                {/* Footer */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {lastSyncTime && (
                            <>
                                <RefreshCw size={13} />
                                עדכון אחרון: {formatDate(lastSyncTime)}
                            </>
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filteredSuppliers.length > 0 && (
                            searchTerm
                                ? `מציג ${filteredSuppliers.length} מתוך ${suppliers.length} ספקים`
                                : `סה"כ ${suppliers.length} ספקים`
                        )}
                    </Typography>
                </Box>
            </StyledCard>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
};

export default SuppliersTab;
